// Push Notifications utility for PWA
// Handles requesting permission, subscribing, and showing notifications

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  data?: Record<string, unknown>;
  requireInteraction?: boolean;
}

/**
 * Check if push notifications are supported
 */
export const isPushSupported = (): boolean => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Show a notification using the Service Worker
 */
export const showNotification = async (options: NotificationOptions): Promise<boolean> => {
  const permission = getNotificationPermission();
  
  if (permission !== 'granted') {
    console.log('Notification permission not granted');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification(options.title, {
      body: options.body,
      icon: options.icon || '/icon-192.png',
      badge: '/icon-192.png',
      tag: options.tag,
      data: options.data,
      requireInteraction: options.requireInteraction || false,
    });

    return true;
  } catch (error) {
    console.error('Error showing notification:', error);
    return false;
  }
};

/**
 * Show sync status notification
 */
export const showSyncNotification = async (
  type: 'complete' | 'error' | 'conflict',
  details?: string
): Promise<boolean> => {
  const notifications = {
    complete: {
      title: 'Sync Complete',
      body: details || 'All your changes have been synced.',
      tag: 'sync-status',
    },
    error: {
      title: 'Sync Failed',
      body: details || 'Failed to sync changes. Will retry automatically.',
      tag: 'sync-status',
    },
    conflict: {
      title: 'Sync Conflict',
      body: details || 'A sync conflict was detected. Please review.',
      tag: 'sync-conflict',
      requireInteraction: true,
    },
  };

  return showNotification(notifications[type]);
};

/**
 * Schedule a script reminder notification
 */
export const scheduleScriptReminder = (
  scriptTitle: string,
  reminderTime: Date,
  scriptId: string
): number => {
  const now = Date.now();
  const delay = reminderTime.getTime() - now;

  if (delay <= 0) {
    console.log('Reminder time is in the past');
    return -1;
  }

  const timerId = window.setTimeout(async () => {
    await showNotification({
      title: 'Script Reminder',
      body: `Time to practice: "${scriptTitle}"`,
      tag: `reminder-${scriptId}`,
      data: { scriptId, type: 'reminder' },
      requireInteraction: true,
    });
  }, delay);

  // Store reminder in localStorage for persistence
  const reminders = getStoredReminders();
  reminders.push({
    id: timerId,
    scriptId,
    scriptTitle,
    reminderTime: reminderTime.toISOString(),
  });
  localStorage.setItem('script-reminders', JSON.stringify(reminders));

  return timerId;
};

/**
 * Cancel a scheduled reminder
 */
export const cancelScriptReminder = (timerId: number): void => {
  window.clearTimeout(timerId);
  
  const reminders = getStoredReminders();
  const filtered = reminders.filter((r) => r.id !== timerId);
  localStorage.setItem('script-reminders', JSON.stringify(filtered));
};

/**
 * Get all stored reminders
 */
export const getStoredReminders = (): Array<{
  id: number;
  scriptId: string;
  scriptTitle: string;
  reminderTime: string;
}> => {
  try {
    const stored = localStorage.getItem('script-reminders');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Clear expired reminders from storage
 */
export const clearExpiredReminders = (): void => {
  const reminders = getStoredReminders();
  const now = Date.now();
  const valid = reminders.filter((r) => new Date(r.reminderTime).getTime() > now);
  localStorage.setItem('script-reminders', JSON.stringify(valid));
};

/**
 * Listen for notification clicks from Service Worker
 */
export const listenForNotificationClicks = (
  callback: (data: Record<string, unknown>) => void
): (() => void) => {
  // Check if serviceWorker is available
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker) {
    return () => {}; // Return no-op cleanup function
  }

  const handler = (event: MessageEvent) => {
    if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
      callback(event.data.payload);
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);

  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
};
